/**
 * QBR Briefs phase — per-rep quarterly review prep.
 *
 * Three views:
 *   1. List view: caller's existing briefs + a "+ Generate brief"
 *      action that opens the date-picker modal.
 *   2. Detail view: the merged brief (AI output + seller overrides)
 *      with the eight sections (quarter summary, wins, losses,
 *      active pipeline, at risk, patterns + lessons, talking points,
 *      discussion questions).
 *   3. Presenter mode: full-screen one-section-at-a-time, click
 *      next/prev to walk through during the actual QBR meeting.
 *
 * Backend: /api/qbr-briefs — see backend/routes/qbrBriefs.js.
 */

import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

// Default period = last calendar quarter ending today. Pickable by
// the seller; this is just a sensible starting offer.
function defaultPeriod() {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  start.setDate(start.getDate() - 90);
  return {
    periodStart: start.toISOString().slice(0, 10),
    periodEnd:   end.toISOString().slice(0, 10),
  };
}

function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return String(d); }
}

function fmtMoney(n) {
  if (n == null) return '';
  return '$' + Number(n).toLocaleString();
}

export default function QbrBriefsPhase({ authToken, currentUserId, isCompanyAdmin }) {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedBriefId, setSelectedBriefId] = useState(null);
  const [presenterMode, setPresenterMode] = useState(false);

  const loadBriefs = async () => {
    setLoading(true);
    setError(null);
    const url = `${API_BASE}/api/qbr-briefs`;
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        // Echo the URL we hit + the response status to the console
        // so a 404 we can't reproduce ourselves becomes diagnosable
        // from the user's DevTools without asking them to inspect
        // every header. Surfaced on-screen too so the seller knows
        // why nothing showed up.
        console.error('[QBR] list load failed', { url, status: r.status });
        throw new Error(`Load failed (${r.status}) — ${url}`);
      }
      const body = await r.json();
      setBriefs(body.briefs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadBriefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Detail view
  if (selectedBriefId) {
    return (
      <BriefDetailView
        briefId={selectedBriefId}
        authToken={authToken}
        onBack={() => { setSelectedBriefId(null); setPresenterMode(false); }}
        presenterMode={presenterMode}
        setPresenterMode={setPresenterMode}
      />
    );
  }

  // List view
  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--room-text-strong, #0f172a)' }}>QBR Briefs</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>
            AI-generated rollup of every deal you closed, slipped, or kept in pipeline over a date range. Generate one before each QBR; present from it during the meeting.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setGenerateOpen(true)}
          style={primaryBtn}
        >
          + Generate brief
        </button>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>Loading briefs…</div>
      ) : briefs.length === 0 ? (
        <div style={{ background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 12, padding: 40, textAlign: 'center', color: 'var(--room-text-muted, #64748b)' }}>
          <div style={{ fontSize: 14, marginBottom: 6, color: 'var(--room-text-strong, #0f172a)', fontWeight: 600 }}>No briefs yet</div>
          <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 480, margin: '0 auto' }}>
            Click <strong>Generate brief</strong> to roll up your last 90 days of deal activity. Takes ~30 seconds. You can re-generate any time after adding new deals.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {briefs.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setSelectedBriefId(b.id)}
              style={briefRowStyle}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--room-border, #e2e8f0)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong, #0f172a)', marginBottom: 4 }}>
                  {fmtDate(b.periodStart)} — {fmtDate(b.periodEnd)}
                </div>
                {b.headline && (
                  <div style={{ fontSize: 13, color: 'var(--room-text-body, #334155)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {b.headline}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', whiteSpace: 'nowrap' }}>
                Generated {fmtDate(b.generatedAt)}
              </div>
            </button>
          ))}
        </div>
      )}

      {generateOpen && (
        <GenerateModal
          authToken={authToken}
          onClose={() => setGenerateOpen(false)}
          onGenerated={(briefId) => { setGenerateOpen(false); setSelectedBriefId(briefId); loadBriefs(); }}
        />
      )}
    </div>
  );
}

// ─── Generate modal ───────────────────────────────────────────────

function GenerateModal({ authToken, onClose, onGenerated }) {
  const def = defaultPeriod();
  const [periodStart, setPeriodStart] = useState(def.periodStart);
  const [periodEnd, setPeriodEnd] = useState(def.periodEnd);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!periodStart || !periodEnd) {
      setError('Pick both a start and end date.');
      return;
    }
    if (periodStart >= periodEnd) {
      setError('Start date must be before end date.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/qbr-briefs/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ periodStart, periodEnd }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Generate failed (${r.status})`);
      onGenerated(body.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Generate a QBR brief" onClose={onClose} disabled={busy}>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--room-text-body, #475569)', lineHeight: 1.6 }}>
        Rolls up every closed-won, closed-lost, and in-pipeline deal in the date range below. Takes ~20-40 seconds depending on deal count.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Field label="From">
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} disabled={busy} style={inputStyle} />
        </Field>
        <Field label="To">
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} disabled={busy} style={inputStyle} />
        </Field>
      </div>
      <div style={{ marginBottom: 14 }}>
        <span style={{ fontSize: 11, color: 'var(--room-text-muted, #64748b)', fontWeight: 500 }}>Quick presets:</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {[
            { label: 'Last 30 days', days: 30 },
            { label: 'Last 60 days', days: 60 },
            { label: 'Last 90 days', days: 90 },
            { label: 'Last 6 months', days: 180 },
          ].map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => {
                const end = new Date();
                const start = new Date(end);
                start.setDate(start.getDate() - p.days);
                setPeriodStart(start.toISOString().slice(0, 10));
                setPeriodEnd(end.toISOString().slice(0, 10));
              }}
              disabled={busy}
              style={presetBtn}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {error && <div style={errorStyle}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" onClick={onClose} disabled={busy} style={secondaryBtn}>Cancel</button>
        <button type="button" onClick={submit} disabled={busy} style={busy ? primaryBtnDisabled : primaryBtn}>
          {busy ? 'Generating…' : 'Generate'}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Detail view ──────────────────────────────────────────────────

function BriefDetailView({ briefId, authToken, onBack, presenterMode, setPresenterMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('summary');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/qbr-briefs/${briefId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const body = await r.json();
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [briefId]);

  const downloadPdf = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/qbr-briefs/${briefId}.pdf`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`PDF download failed (${r.status})`);
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `qbr-brief-${briefId}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--room-text-muted, #64748b)' }}>Loading brief…</div>;
  if (error)   return <div style={{ padding: 24 }}><div style={errorStyle}>{error}</div></div>;
  if (!data)   return <div style={{ padding: 24, color: 'var(--room-text-muted, #64748b)' }}>Brief not found.</div>;

  const brief = data.brief || {};
  const sections = [
    { id: 'summary',     label: 'Quarter at a glance' },
    { id: 'wins',        label: 'Wins' },
    { id: 'losses',      label: 'Losses' },
    { id: 'pipeline',    label: 'Active pipeline' },
    { id: 'at_risk',     label: 'At risk' },
    { id: 'patterns',    label: 'Patterns + lessons' },
    { id: 'talking',     label: 'Talking points' },
    { id: 'questions',   label: "Manager's Q&A" },
  ];

  // Presenter mode — single section, full-screen, click prev/next.
  if (presenterMode) {
    return <PresenterMode brief={brief} sections={sections} onExit={() => setPresenterMode(false)} sellerLabel={data.targetUser?.name || ''} />;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={ghostBtn}>← Back</button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ margin: 0, fontSize: 22, color: 'var(--room-text-strong, #0f172a)' }}>
            {fmtDate(data.periodStart)} — {fmtDate(data.periodEnd)}
          </h1>
          <div style={{ fontSize: 12, color: 'var(--room-text-subtle, #94a3b8)' }}>
            {data.targetUser?.name && `For ${data.targetUser.name} · `}Generated {fmtDate(data.generatedAt)}
          </div>
        </div>
        <button type="button" onClick={downloadPdf} style={secondaryBtn}>Download PDF</button>
        <button type="button" onClick={() => setPresenterMode(true)} style={primaryBtn}>Present</button>
      </div>

      {brief.headline && (
        <div style={{
          padding: '14px 18px', borderRadius: 12, marginBottom: 18,
          background: '#f5f3ff', borderLeft: '4px solid #7c3aed',
          fontSize: 15, fontWeight: 600, color: '#1e1b4b', lineHeight: 1.5,
        }}>
          {brief.headline}
        </div>
      )}

      {/* Section nav (sticky chips) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              setActiveSection(s.id);
              const el = document.getElementById(`qbr-section-${s.id}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            style={{
              padding: '5px 12px', borderRadius: 999,
              border: '1px solid ' + (activeSection === s.id ? '#7c3aed' : 'var(--room-border, #cbd5e1)'),
              background: activeSection === s.id ? '#7c3aed' : '#fff',
              color: activeSection === s.id ? '#fff' : 'var(--room-text-body, #334155)',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <SectionSummary brief={brief} />
      <SectionWins brief={brief} />
      <SectionLosses brief={brief} />
      <SectionPipeline brief={brief} />
      <SectionAtRisk brief={brief} />
      <SectionPatterns brief={brief} />
      <SectionTalking brief={brief} />
      <SectionQuestions brief={brief} />
    </div>
  );
}

// ─── Presenter mode ───────────────────────────────────────────────

function PresenterMode({ brief, sections, onExit, sellerLabel }) {
  const [idx, setIdx] = useState(0);
  const section = sections[idx];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onExit();
      else if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        setIdx((i) => Math.min(i + 1, sections.length - 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        setIdx((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.length]);

  const renderSection = () => {
    switch (section.id) {
      case 'summary':   return <SectionSummary brief={brief} presenter />;
      case 'wins':      return <SectionWins brief={brief} presenter />;
      case 'losses':    return <SectionLosses brief={brief} presenter />;
      case 'pipeline':  return <SectionPipeline brief={brief} presenter />;
      case 'at_risk':   return <SectionAtRisk brief={brief} presenter />;
      case 'patterns':  return <SectionPatterns brief={brief} presenter />;
      case 'talking':   return <SectionTalking brief={brief} presenter />;
      case 'questions': return <SectionQuestions brief={brief} presenter />;
      default: return null;
    }
  };

  // Lock body scroll while presenter is mounted so the underlying
  // page doesn't scroll behind the overlay. Restored on unmount.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Build the overlay JSX, then portal it directly to document.body
  // so it escapes any ancestor that creates a containing block
  // (transform, filter, perspective, etc.) — without the portal, the
  // .app.has-left-nav wrapper's padding-left was constraining the
  // "position: fixed; inset: 0" presenter to the area to the right
  // of the sidebar instead of covering the full viewport. The
  // LeftNav (z-index 100) ended up rendering ABOVE the slide content
  // because the slide's containing block had been re-rooted to the
  // padded area, making its width less than 100vw. Portaling
  // directly to body resets the containing block to the viewport.
  const overlay = (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: '#020617', color: 'var(--room-surface, #f8fafc)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        padding: '14px 24px',
        display: 'flex', alignItems: 'center', gap: 12,
        borderBottom: '1px solid rgba(248, 250, 252, 0.08)',
      }}>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: 'rgba(248, 250, 252, 0.6)',
        }}>
          QBR · {sellerLabel}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'rgba(248, 250, 252, 0.55)' }}>
            {idx + 1} / {sections.length}
          </span>
          <button type="button" onClick={onExit} style={presenterExitBtn}>Exit (Esc)</button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '40px 60px', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: '#a78bfa',
          textTransform: 'uppercase', letterSpacing: '0.18em',
          marginBottom: 8,
        }}>
          Section {idx + 1} of {sections.length}
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 800, color: 'var(--room-surface, #f8fafc)', margin: '0 0 24px', letterSpacing: '-0.5px' }}>
          {section.label}
        </h1>
        <div className="qbr-presenter-section" style={{ color: 'var(--room-border, #e2e8f0)' }}>
          {renderSection()}
        </div>
      </div>

      <div style={{
        padding: '14px 24px',
        borderTop: '1px solid rgba(248, 250, 252, 0.08)',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button type="button" onClick={() => setIdx((i) => Math.max(i - 1, 0))} disabled={idx === 0} style={presenterNavBtn(idx === 0)}>
          ← Prev
        </button>
        <div style={{ flex: 1, fontSize: 12, color: 'rgba(248, 250, 252, 0.55)', textAlign: 'center' }}>
          ← / → arrow keys to navigate
        </div>
        <button type="button" onClick={() => setIdx((i) => Math.min(i + 1, sections.length - 1))} disabled={idx === sections.length - 1} style={presenterNavBtn(idx === sections.length - 1)}>
          Next →
        </button>
      </div>
    </div>
  );

  // SSR guard — document is undefined on the server, but Create
  // React App / Vercel renders this client-side only so the guard is
  // belt-and-suspenders for hydration mismatch protection.
  if (typeof document === 'undefined') return overlay;
  return createPortal(overlay, document.body);
}

// ─── Sections ─────────────────────────────────────────────────────

function SectionShell({ id, title, children, presenter }) {
  if (presenter) return <div>{children}</div>;
  return (
    <section
      id={`qbr-section-${id}`}
      style={{
        background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)',
        borderRadius: 14, padding: 22, marginBottom: 16,
      }}
    >
      <h2 style={{
        margin: '0 0 14px', fontSize: 12, fontWeight: 700,
        color: 'var(--room-text-body, #475569)', textTransform: 'uppercase', letterSpacing: '0.08em',
      }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function SectionSummary({ brief, presenter }) {
  const s = brief.quarter_summary || {};
  const stats = [
    { label: 'Closed-won',   value: s.deals_closed_won != null ? s.deals_closed_won : '—' },
    { label: 'Closed-lost',  value: s.deals_closed_lost != null ? s.deals_closed_lost : '—' },
    { label: 'In pipeline',  value: s.deals_in_pipeline != null ? s.deals_in_pipeline : '—' },
    { label: 'Win rate',     value: s.win_rate_pct != null ? `${s.win_rate_pct}%` : '—' },
    { label: 'ARR booked',   value: s.arr_booked_usd ? fmtMoney(s.arr_booked_usd) : '$0' },
    { label: 'Avg deal',     value: s.avg_deal_size_usd ? fmtMoney(s.avg_deal_size_usd) : '$0' },
  ];
  return (
    <SectionShell id="summary" title="Quarter at a glance" presenter={presenter}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12,
        marginBottom: s.prior_period_comparison ? 14 : 0,
      }}>
        {stats.map((st) => (
          <div key={st.label} style={statCard(presenter)}>
            <div style={{
              fontSize: presenter ? 36 : 26, fontWeight: 700,
              color: presenter ? 'var(--room-surface, #f8fafc)' : 'var(--room-text-strong, #0f172a)',
              fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px', lineHeight: 1,
            }}>
              {st.value}
            </div>
            <div style={{
              fontSize: 10.5, fontWeight: 600,
              color: presenter ? 'rgba(248, 250, 252, 0.6)' : 'var(--room-text-muted, #64748b)',
              textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 6,
            }}>
              {st.label}
            </div>
          </div>
        ))}
      </div>
      {s.prior_period_comparison && (
        <div style={{
          padding: '10px 14px', borderRadius: 8,
          background: presenter ? 'rgba(124, 58, 237, 0.18)' : 'var(--room-border-soft, #f1f5f9)',
          color: presenter ? '#e9d5ff' : 'var(--room-text-body, #475569)',
          fontSize: 13, lineHeight: 1.55,
        }}>
          {s.prior_period_comparison}
        </div>
      )}
    </SectionShell>
  );
}

function DealRow({ deal, kind, presenter }) {
  const colors = kind === 'won' ? { accent: '#10b981', bg: presenter ? 'rgba(16, 185, 129, 0.1)' : '#ecfdf5' }
    : kind === 'lost' ? { accent: '#ef4444', bg: presenter ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2' }
    : kind === 'risk' ? { accent: '#f59e0b', bg: presenter ? 'rgba(245, 158, 11, 0.1)' : '#fffbeb' }
    : { accent: '#3b82f6', bg: presenter ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff' };
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 10,
      background: colors.bg, borderLeft: `3px solid ${colors.accent}`,
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <strong style={{ fontSize: 14, color: presenter ? 'var(--room-surface, #f8fafc)' : 'var(--room-text-strong, #0f172a)' }}>{deal.prospect}</strong>
        {deal.deal_value_usd ? (
          <span style={{ fontSize: 12, color: presenter ? '#a7f3d0' : colors.accent, fontWeight: 600 }}>
            {fmtMoney(deal.deal_value_usd)}
          </span>
        ) : null}
        {deal.closed_at && <span style={{ fontSize: 11, color: presenter ? 'rgba(248, 250, 252, 0.5)' : 'var(--room-text-subtle, #94a3b8)' }}>{deal.closed_at}</span>}
        {deal.expected_close && <span style={{ fontSize: 11, color: presenter ? 'rgba(248, 250, 252, 0.5)' : 'var(--room-text-subtle, #94a3b8)' }}>Expected {deal.expected_close}</span>}
        {deal.stage && <span style={{ fontSize: 10, fontWeight: 700, color: presenter ? '#bfdbfe' : '#1d4ed8', background: presenter ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe', padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{deal.stage}</span>}
      </div>
      {deal.why_we_won && <FieldLine label="Why" value={deal.why_we_won} presenter={presenter} />}
      {deal.why_we_lost && <FieldLine label="Why" value={deal.why_we_lost} presenter={presenter} />}
      {deal.lesson && <FieldLine label="Lesson" value={deal.lesson} presenter={presenter} />}
      {deal.last_engagement_summary && <FieldLine label="Engagement" value={deal.last_engagement_summary} presenter={presenter} />}
      {deal.next_step && <FieldLine label="Next" value={deal.next_step} presenter={presenter} />}
      {deal.risk && <FieldLine label="Risk" value={deal.risk} presenter={presenter} />}
      {deal.rescue_plan && <FieldLine label="Plan" value={deal.rescue_plan} presenter={presenter} />}
    </div>
  );
}

function FieldLine({ label, value, presenter }) {
  return (
    <div style={{ fontSize: 13, lineHeight: 1.55, marginTop: 2, color: presenter ? 'var(--room-border, #e2e8f0)' : 'var(--room-text-body, #334155)' }}>
      <strong style={{ color: presenter ? 'rgba(248, 250, 252, 0.7)' : 'var(--room-text-muted, #64748b)' }}>{label}: </strong>
      {value}
    </div>
  );
}

function SectionWins({ brief, presenter }) {
  const items = brief.wins || [];
  if (items.length === 0) return null;
  return (
    <SectionShell id="wins" title="Wins" presenter={presenter}>
      {items.map((w, i) => <DealRow key={i} deal={w} kind="won" presenter={presenter} />)}
    </SectionShell>
  );
}
function SectionLosses({ brief, presenter }) {
  const items = brief.losses || [];
  if (items.length === 0) return null;
  return (
    <SectionShell id="losses" title="Losses" presenter={presenter}>
      {items.map((w, i) => <DealRow key={i} deal={w} kind="lost" presenter={presenter} />)}
    </SectionShell>
  );
}
function SectionPipeline({ brief, presenter }) {
  const items = brief.active_pipeline || [];
  if (items.length === 0) return null;
  return (
    <SectionShell id="pipeline" title="Active pipeline" presenter={presenter}>
      {items.map((w, i) => <DealRow key={i} deal={w} kind="open" presenter={presenter} />)}
    </SectionShell>
  );
}
function SectionAtRisk({ brief, presenter }) {
  const items = brief.at_risk || [];
  if (items.length === 0) return null;
  return (
    <SectionShell id="at_risk" title="At risk" presenter={presenter}>
      {items.map((w, i) => <DealRow key={i} deal={w} kind="risk" presenter={presenter} />)}
    </SectionShell>
  );
}
function SectionPatterns({ brief, presenter }) {
  const items = brief.patterns_and_lessons || [];
  if (items.length === 0) return null;
  return (
    <SectionShell id="patterns" title="Patterns + lessons" presenter={presenter}>
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.65, color: presenter ? 'var(--room-border, #e2e8f0)' : 'var(--room-text-strong, #1e293b)' }}>
        {items.map((p, i) => (
          <li key={i} style={{ marginBottom: 8 }}>{typeof p === 'string' ? p : (p.text || JSON.stringify(p))}</li>
        ))}
      </ul>
    </SectionShell>
  );
}
function SectionTalking({ brief, presenter }) {
  const items = brief.talking_points || [];
  if (items.length === 0) return null;
  return (
    <SectionShell id="talking" title="Talking points" presenter={presenter}>
      {items.map((t, i) => (
        <div key={i} style={{ padding: '8px 0', borderBottom: i === items.length - 1 ? 'none' : `1px solid ${presenter ? 'rgba(248, 250, 252, 0.1)' : 'var(--room-border-soft, #f1f5f9)'}` }}>
          {t.topic && (
            <div style={{
              fontSize: 10, fontWeight: 700,
              color: presenter ? '#a78bfa' : '#7c3aed',
              textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
            }}>
              {t.topic}
            </div>
          )}
          <div style={{ fontSize: 14, lineHeight: 1.55, color: presenter ? 'var(--room-surface, #f8fafc)' : 'var(--room-text-strong, #1e293b)' }}>
            {t.point}
          </div>
        </div>
      ))}
    </SectionShell>
  );
}
function SectionQuestions({ brief, presenter }) {
  const items = brief.discussion_questions || [];
  if (items.length === 0) return null;
  return (
    <SectionShell id="questions" title="Manager's likely questions" presenter={presenter}>
      {items.map((q, i) => (
        <div key={i} style={{ padding: '10px 0', borderBottom: i === items.length - 1 ? 'none' : `1px solid ${presenter ? 'rgba(248, 250, 252, 0.1)' : 'var(--room-border-soft, #f1f5f9)'}` }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: presenter ? 'var(--room-surface, #f8fafc)' : 'var(--room-text-strong, #0f172a)', marginBottom: 4 }}>
            {q.question}
          </div>
          {q.preempt && (
            <div style={{
              fontSize: 13, lineHeight: 1.55,
              color: presenter ? 'var(--room-border, #e2e8f0)' : 'var(--room-text-body, #475569)',
              paddingLeft: 12, borderLeft: `2px solid ${presenter ? '#a78bfa' : '#c4b5fd'}`,
            }}>
              {q.preempt}
            </div>
          )}
        </div>
      ))}
    </SectionShell>
  );
}

// ─── Modal shell ───────────────────────────────────────────────────

function ModalShell({ title, onClose, disabled, children }) {
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !disabled) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        background: 'var(--room-card, #fff)', borderRadius: 14,
        width: 'min(560px, 100%)',
        maxHeight: '92vh', overflow: 'auto',
        padding: 22, boxShadow: '0 20px 60px rgba(15,23,42,0.3)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: 'var(--room-text-strong, #0f172a)' }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            style={{
              background: 'none', border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontSize: 22, color: 'var(--room-text-muted, #64748b)', padding: 4,
              opacity: disabled ? 0.4 : 1,
            }}
          >×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--room-text-body, #475569)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: '1px solid #cbd5e1', fontSize: 13, fontFamily: 'inherit',
  boxSizing: 'border-box',
};
const errorStyle = {
  padding: '10px 14px', borderRadius: 10,
  background: '#fee2e2', border: '1px solid #fecaca',
  color: '#991b1b', fontSize: 13, marginBottom: 12,
};
const primaryBtn = {
  padding: '9px 18px', borderRadius: 10, border: 'none',
  background: 'linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%)',
  color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
  cursor: 'pointer', boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
};
const primaryBtnDisabled = { ...primaryBtn, opacity: 0.55, cursor: 'not-allowed' };
const secondaryBtn = {
  padding: '9px 16px', borderRadius: 10,
  border: '1px solid #cbd5e1', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)',
  fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
};
const ghostBtn = {
  padding: '6px 12px', borderRadius: 8,
  border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #475569)',
  fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
};
const presetBtn = {
  padding: '4px 10px', borderRadius: 6,
  border: '1px solid #cbd5e1', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)',
  fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
};
const briefRowStyle = {
  display: 'flex', alignItems: 'center', gap: 14,
  background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 12,
  padding: 16, cursor: 'pointer',
  textAlign: 'left', fontFamily: 'inherit',
  transition: 'border-color 0.14s ease, transform 0.14s ease',
};
function statCard(presenter) {
  return {
    padding: '14px 16px', borderRadius: 10,
    background: presenter ? 'rgba(248, 250, 252, 0.06)' : 'var(--room-surface, #f8fafc)',
    border: `1px solid ${presenter ? 'rgba(248, 250, 252, 0.1)' : 'var(--room-border, #e2e8f0)'}`,
  };
}
const presenterExitBtn = {
  padding: '6px 14px', borderRadius: 8,
  border: '1px solid rgba(248, 250, 252, 0.15)',
  background: 'rgba(248, 250, 252, 0.06)',
  color: 'var(--room-border-soft, #f1f5f9)',
  fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
};
function presenterNavBtn(disabled) {
  return {
    padding: '8px 18px', borderRadius: 10,
    border: '1px solid rgba(248, 250, 252, 0.15)',
    background: disabled ? 'rgba(248, 250, 252, 0.03)' : 'rgba(248, 250, 252, 0.08)',
    color: disabled ? 'rgba(248, 250, 252, 0.3)' : 'var(--room-border-soft, #f1f5f9)',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
